import React, { useState, useEffect } from 'react';
import { useLanguage } from '@contexts/LanguageContext';
import { useNotification } from '@hooks/useNotification';
import permissionService from '@services/permissionService';
import ErrorDisplay from './components/ErrorDisplay';
import './UserForm.scss';

const UserForm = ({ data, onSubmit, onCancel, isEditing = false, loading: externalLoading = false }) => {
    const { t } = useLanguage();
    const notify = useNotification();
    const [formData, setFormData] = useState({
        role: 'USER',
        is_active: true
    });
    const [permissions, setPermissions] = useState([]);
    const [locations, setLocations] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [permissionForm, setPermissionForm] = useState({
        permission_type: '',
        target_location_id: '',
        target_user_id: '',
        notes: ''
    });
    const [showPermissionForm, setShowPermissionForm] = useState(false);
    const [originalData, setOriginalData] = useState(null);

    const userRoles = [
        { value: 'USER', label: t('admin.roles.user') || 'User' },
        { value: 'ADMIN', label: t('admin.roles.admin') || 'Admin' },
        { value: 'SUPER_ADMIN', label: t('admin.roles.super_admin') || 'Super Admin' }
    ];

    const permissionTypes = [
        { value: 'LOCATION_MANAGEMENT', label: t('permission.types.LOCATION_MANAGEMENT') || 'Location Management' },
        { value: 'BORROW_FOR_USER', label: t('permission.types.BORROW_FOR_USER') || 'Borrow for User' },
        { value: 'RESOURCE_MANAGEMENT', label: t('permission.types.RESOURCE_MANAGEMENT') || 'Resource Management' }
    ];

    // Initialize form data when user data changes
    useEffect(() => {
        if (data) {
            const initialData = {
                role: data.role || 'USER',
                is_active: data.is_active !== undefined ? data.is_active : true
            };
            setFormData(initialData);
            setOriginalData(initialData);
        }
    }, [data]);

    // Load user permissions, locations, and users when editing
    useEffect(() => {
        if (isEditing && data?.id) {
            loadUserData();
        }
    }, [isEditing, data?.id]);

    const loadUserData = async () => {
        try {
            setLoading(true);
            const [permissionsResponse, locationsResponse, usersResponse] = await Promise.all([
                permissionService.getUserPermissions(data.id),
                permissionService.getLocations(),
                permissionService.getActiveUsers()
            ]);

            setPermissions(permissionsResponse);
            setLocations(locationsResponse);
            setUsers(usersResponse.filter(user => user.id !== data.id)); // Exclude current user
        } catch (err) {
            console.error('Failed to load user data:', err);
            setError(t('admin.users.load_failed'));
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handlePermissionFormChange = (e) => {
        const { name, value } = e.target;
        setPermissionForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (externalLoading || loading) return;

        try {
            await onSubmit(formData);
        } catch (err) {
            setError(err.response?.data?.detail || t('common.error_occurred'));
        }
    };

    const handleGrantPermission = async () => {
        if (!permissionForm.permission_type) {
            notify.error(t('admin.permissions.select_type'));
            return;
        }

        try {
            setLoading(true);
            await permissionService.grantPermission(
                data.id,
                permissionForm.permission_type,
                permissionForm.target_location_id || null,
                permissionForm.target_user_id || null,
                permissionForm.notes || null
            );

            notify.success(t('admin.permissions.granted'));
            setPermissionForm({ permission_type: '', target_location_id: '', target_user_id: '', notes: '' });
            setShowPermissionForm(false);

            // Reload permissions
            const updatedPermissions = await permissionService.getUserPermissions(data.id);
            setPermissions(updatedPermissions);
        } catch (err) {
            console.error('Failed to grant permission:', err);
            notify.error(err.response?.data?.detail || t('admin.permissions.grant_failed'));
        } finally {
            setLoading(false);
        }
    };

    const handleRevokePermission = async (permission) => {
        try {
            setLoading(true);
            await permissionService.revokePermission(
                data.id,
                permission.permission_type,
                permission.target_location_id,
                permission.target_user_id
            );

            notify.success(t('admin.permissions.revoked'));

            // Reload permissions
            const updatedPermissions = await permissionService.getUserPermissions(data.id);
            setPermissions(updatedPermissions);
        } catch (err) {
            console.error('Failed to revoke permission:', err);
            notify.error(err.response?.data?.detail || t('admin.permissions.revoke_failed'));
        } finally {
            setLoading(false);
        }
    };

    const getLocationName = (locationId) => {
        const location = locations.find(loc => loc.id === locationId);
        return location?.name || `Location #${locationId}`;
    };

    const getUserName = (userId) => {
        const user = users.find(u => u.id === userId);
        return user?.name || user?.email || `User #${userId}`;
    };

    const hasFormChanged = () => {
        if (!originalData) return false;
        return JSON.stringify(formData) !== JSON.stringify(originalData);
    };

    const isFormValid = () => {
        if (isEditing) {
            return hasFormChanged();
        }
        return true; // For new users, form is always valid since no required fields
    };

    return (
        <div className="user-form">
            <form onSubmit={handleSubmit}>
                <ErrorDisplay error={error} />

                {/* Basic User Information */}
                <div className="form-section">
                    <h3>{t('admin.users.basic_info')}</h3>

                    {/* Display user name and email as read-only when editing */}
                    {isEditing && data && (
                        <>
                            <div className="form-group">
                                <label>{t('admin.users.name')}</label>
                                <div className="form-display-value">
                                    {data.name || t('common.not_specified')}
                                </div>
                            </div>

                            <div className="form-group">
                                <label>{t('admin.users.email')}</label>
                                <div className="form-display-value">
                                    {data.email || t('common.not_specified')}
                                </div>
                            </div>
                        </>
                    )}

                    <div className="form-group">
                        <label htmlFor="role">
                            {t('admin.users.role')}
                        </label>
                        <select
                            id="role"
                            name="role"
                            value={formData.role}
                            onChange={handleInputChange}
                            disabled={loading || externalLoading}
                        >
                            {userRoles.map(role => (
                                <option key={role.value} value={role.value}>
                                    {role.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group checkbox-group">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                name="is_active"
                                checked={formData.is_active}
                                onChange={handleInputChange}
                                disabled={loading || externalLoading}
                            />
                            <span>{t('admin.users.is_active')}</span>
                        </label>
                    </div>
                </div>

                {/* Permissions Section - only show when editing */}
                {isEditing && data?.id && (
                    <div className="form-section">
                        <div className="section-header">
                            <h3>{t('admin.permissions.title')}</h3>
                            <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => setShowPermissionForm(!showPermissionForm)}
                                disabled={loading}
                            >
                                {showPermissionForm ? t('common.cancel') : t('admin.permissions.add')}
                            </button>
                        </div>

                        {/* Add Permission Form */}
                        {showPermissionForm && (
                            <div className="permission-form">
                                <div className="form-group">
                                    <label htmlFor="permission_type">
                                        {t('admin.permissions.type')} *
                                    </label>
                                    <select
                                        id="permission_type"
                                        name="permission_type"
                                        value={permissionForm.permission_type}
                                        onChange={handlePermissionFormChange}
                                        disabled={loading}
                                    >
                                        <option value="">{t('admin.permissions.select_type')}</option>
                                        {permissionTypes.map(type => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {permissionForm.permission_type === 'LOCATION_MANAGEMENT' && (
                                    <div className="form-group">
                                        <label htmlFor="target_location_id">
                                            {t('admin.permissions.target_location')}
                                        </label>
                                        <select
                                            id="target_location_id"
                                            name="target_location_id"
                                            value={permissionForm.target_location_id}
                                            onChange={handlePermissionFormChange}
                                            disabled={loading}
                                        >
                                            <option value="">{t('admin.permissions.all_locations')}</option>
                                            {locations.map(location => (
                                                <option key={location.id} value={location.id}>
                                                    {location.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {permissionForm.permission_type === 'BORROW_FOR_USER' && (
                                    <div className="form-group">
                                        <label htmlFor="target_user_id">
                                            {t('admin.permissions.target_user')}
                                        </label>
                                        <select
                                            id="target_user_id"
                                            name="target_user_id"
                                            value={permissionForm.target_user_id}
                                            onChange={handlePermissionFormChange}
                                            disabled={loading}
                                        >
                                            <option value="">{t('admin.permissions.all_users')}</option>
                                            {users.map(user => (
                                                <option key={user.id} value={user.id}>
                                                    {user.name || user.email}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                <div className="form-group">
                                    <label htmlFor="notes">
                                        {t('admin.permissions.notes')}
                                    </label>
                                    <textarea
                                        id="notes"
                                        name="notes"
                                        value={permissionForm.notes}
                                        onChange={handlePermissionFormChange}
                                        rows={2}
                                        disabled={loading}
                                    />
                                </div>

                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={handleGrantPermission}
                                    disabled={loading || !permissionForm.permission_type}
                                >
                                    {loading ? t('common.saving') : t('admin.permissions.grant')}
                                </button>
                            </div>
                        )}

                        {/* Existing Permissions List */}
                        <div className="permissions-list">
                            {permissions.length === 0 ? (
                                <p className="no-permissions">{t('permission.noPermissions')}</p>
                            ) : (
                                permissions.map(permission => (
                                    <div key={permission.id} className="permission-item">
                                        <div className="permission-info">
                                            <span className="permission-type">
                                                {permissionTypes.find(p => p.value === permission.permission_type)?.label || permission.permission_type}
                                            </span>
                                            {permission.target_location_id && (
                                                <span className="permission-target">
                                                    → {getLocationName(permission.target_location_id)}
                                                </span>
                                            )}
                                            {permission.target_user_id && (
                                                <span className="permission-target">
                                                    → {getUserName(permission.target_user_id)}
                                                </span>
                                            )}
                                            {permission.notes && (
                                                <span className="permission-notes">
                                                    ({permission.notes})
                                                </span>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-danger btn-sm"
                                            onClick={() => handleRevokePermission(permission)}
                                            disabled={loading}
                                            title={t('admin.permissions.revoke')}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                {/* Form Actions */}
                <div className="form-actions">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="btn btn-secondary"
                        disabled={loading || externalLoading}
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={loading || externalLoading || !isFormValid()}
                    >
                        {(loading || externalLoading) ? t('common.saving') : (isEditing ? t('common.update') : t('common.create'))}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default UserForm;
